import { BuildingOfficeIcon, HomeIcon } from "@heroicons/react/24/outline";
import { ActionType, HomeCareLeadId, PlacementId } from "@sp-crm/core";
import { useQueryClient } from "@tanstack/react-query";
import { trpc, trpcClient } from "components/rpc/trpc";
import { ActionsMenuAction } from "components/ui/actions-menu";
import { fancyAlert } from "components/ui/fancy-alert";
import { fancyConfirm } from "components/ui/fancy-confirm";
import React from "react";
import { useHistory } from "react-router-dom";
import { useCrmMode, useFeature, useIsAllowed } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { engagementPath } from "./engagement-tabs";

const useInvalidateEngagements = (): (() => Promise<void>) => {
    const trpcOptions = trpc.useTRPC();
    const queryClient = useQueryClient();
    const pathKey = trpcOptions.clientEngagements.list.pathKey();
    return () => queryClient.invalidateQueries({ queryKey: pathKey });
};

interface StartEngagementParams {
    eventType: string;
    confirmTitle: string;
    confirmMessage: string;
    confirmLabel: string;
    errorMessage: string;
    start: () => Promise<string>;
}

const useStartEngagement = (params: StartEngagementParams): (() => Promise<void>) => {
    const invalidateEngagements = useInvalidateEngagements();
    const { push } = useHistory();
    const pendingRef = React.useRef(false);

    return async () => {
        if (pendingRef.current) {
            return;
        }
        pendingRef.current = true;
        try {
            handleEvent("client-action", { type: params.eventType });
            const confirmed = await fancyConfirm(
                params.confirmTitle,
                params.confirmMessage,
                params.confirmLabel,
                "Cancel",
            );
            if (!confirmed) {
                return;
            }
            const path = await params.start();
            await invalidateEngagements();
            push(path);
        } catch {
            await fancyAlert("Error", params.errorMessage);
        } finally {
            pendingRef.current = false;
        }
    };
};

interface StartHomeCareMenuActionProps {
    placementId: PlacementId;
    clientName: string;
}

export const StartHomeCareMenuAction: React.FC<StartHomeCareMenuActionProps> = props => {
    const { placementId, clientName } = props;
    const placementDetailVNext = useFeature("placementDetailVNext");
    const { mode } = useCrmMode();
    const canCreate = useIsAllowed(ActionType.CreateHomeCareLead);
    const displayName = clientName || "this client";

    const onClick = useStartEngagement({
        eventType: "startHomeCare",
        confirmTitle: "Start home care?",
        confirmMessage: `Start home care for ${displayName}? This creates a new home care client alongside the current placement.`,
        confirmLabel: "Yes, start home care",
        errorMessage: "Failed to start home care. Please try again.",
        start: async () => {
            const result = await trpcClient.clientEngagements.startHomeCare.mutate({
                placementId,
            });
            return engagementPath({ type: "homeCareLead", id: result.homeCareLeadId });
        },
    });

    return (
        <ActionsMenuAction
            Icon={HomeIcon}
            menuHidden={mode !== "combined" || !canCreate || !placementDetailVNext}
            onClick={() => void onClick()}>
            Start home care
        </ActionsMenuAction>
    );
};

interface StartPlacementMenuActionProps {
    homeCareLeadId: HomeCareLeadId;
    clientName: string;
}

export const StartPlacementMenuAction: React.FC<
    StartPlacementMenuActionProps
> = props => {
    const { homeCareLeadId, clientName } = props;
    const homeCareLeadDetailVNext = useFeature("homeCareLeadDetailVNext");
    const { mode } = useCrmMode();
    const canCreate = useIsAllowed(ActionType.CreateClient);
    const displayName = clientName || "this client";

    const onClick = useStartEngagement({
        eventType: "startPlacement",
        confirmTitle: "Start placement?",
        confirmMessage: `Start a placement for ${displayName}? This creates a new placement client alongside the current home care client.`,
        confirmLabel: "Yes, start placement",
        errorMessage: "Failed to start placement. Please try again.",
        start: async () => {
            const result = await trpcClient.clientEngagements.startPlacement.mutate({
                homeCareLeadId,
            });
            return engagementPath({ type: "placement", id: result.placementId });
        },
    });

    return (
        <ActionsMenuAction
            Icon={BuildingOfficeIcon}
            menuHidden={mode !== "combined" || !canCreate || !homeCareLeadDetailVNext}
            onClick={() => void onClick()}>
            Start placement
        </ActionsMenuAction>
    );
};
